import FrontEndApiService from './frontend.api.service';

class AuthError extends Error {
    constructor(errorCode, message) {
        super(message);
        this.name = this.constructor.name;
        this.message = message;
        this.errorCode = errorCode;
    };
}

const AuthUrlMap = {
    LOGIN: '/auth/login',
    LOGIN_SSP_TOKEN: '/auth/login-ssp-token',
    REGISTER: '/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh-token',
    ME: '/auth/me',
    LOGIN_THIRD_PARTY: '/auth/login-third-party',
    THIRD_PARTY_AUTHORIZE: '/partner/third-party-authorize',
    THIRD_PARTY_PERSISTENT_TOKEN: '/partner/third-party-persistent-token',
};

const AuthService = {
    async login(email, password) {
        try {
            const response = email && password
                ? await FrontEndApiService.post(AuthUrlMap.LOGIN, {
                    grantType: 'password',
                    username: email,
                    password: password,
                })
                : {};

            return {
                accessToken: response?.data?.access_token,
                refreshToken: response?.data?.refresh_token,
            };
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }
    },

    async loginBySspToken(uuid) {
        try {
            const response = uuid
                ? await FrontEndApiService.post(AuthUrlMap.LOGIN_SSP_TOKEN, {
                    identifier: uuid,
                })
                : {};

            return {
                accessToken: response?.data?.access_token,
                refreshToken: response?.data?.refresh_token,
            };
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }
    },

    async refreshToken(refreshToken) {
        try {
            const response = refreshToken
                ? await FrontEndApiService.post(AuthUrlMap.REFRESH_TOKEN, {
                    grantType: 'refreshToken',
                    refreshToken: refreshToken,
                })
                : {};

            return {
                accessToken: response?.data?.access_token,
                refreshToken: response?.data?.refresh_token,
            };
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }

    },

    async register(data) {
        try {
            return await FrontEndApiService.post(AuthUrlMap.REGISTER, {
                grantType: 'register',
                ...data,
            });
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }
    },

    logout() {
        FrontEndApiService.unmountRequestInterceptor();
        FrontEndApiService.unmount401Interceptor();
    },

    async me() {
        return await FrontEndApiService.get(AuthUrlMap.ME);
    },

    async thirdPartyAuthorize(clientUuid, authCallbackUrl) {
        try {
            const response = clientUuid
                ? await FrontEndApiService.post(AuthUrlMap.THIRD_PARTY_AUTHORIZE, {
                    clientUuid: clientUuid,
                    authCallbackUrl: authCallbackUrl,
                })
                : {};

            return {
                shouldRedirect: response.data.shouldRedirect,
                redirectUrl: response.data.redirectUrl
            };
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }
    },

    async thirdPartyToken(thirdPartyKey, authCode) {
        try {
            const response = thirdPartyKey && authCode
                ? await FrontEndApiService.post(AuthUrlMap.THIRD_PARTY_PERSISTENT_TOKEN, {
                    grantType: 'authorization_code',
                    thirdPartyKey: thirdPartyKey,
                    authCode: authCode,
                })
                : {};

            return {
                authCode: response?.data?.access_token,
            };
        } catch (error) {
            throw new AuthError(error.response.status, error.response.data.detail);
        }
    },

    async thirdPartyLogin(thirdPartySlug, accessToken) {
        try {
            const response = thirdPartySlug && accessToken
                ? await FrontEndApiService.post(AuthUrlMap.LOGIN_THIRD_PARTY, {
                    thirdPartySlug: thirdPartySlug,
                    accessToken: accessToken,
                })
                : {};

            return {
                accessToken: response?.data?.access_token,
                refreshToken: response?.data?.refresh_token,
                isRevoked: !!response?.data?.is_revoked,
                thirdPartyUuid: response?.data?.third_party_uuid,
                clientUuid: response?.data?.client_uuid,
            }
        } catch (error) {
            let errRsp = error.response || {};
            throw new AuthError(errRsp.status, errRsp.data.detail || errRsp.data.message);
        }
    },
};

export default AuthService;

export {AuthError, AuthUrlMap, AuthService};